import { forwardRef } from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Panel } from "@pilot-protocol/theme/chrome";
import { cn } from "./lib/cn.js";

/**
 * Select — chrome-vocabulary single-choice menu.
 *
 * The trigger mirrors `<Input>` (hairline border, sharp corners, mono
 * body). The dropdown content wraps in a chrome `<Panel variant="solid">`
 * so it inherits the chamfered corners + hairline outline + strong-bg
 * fill that the rest of the chrome system uses for floating surfaces
 * (mirrors `<DialogContent>`).
 */

export const Select = SelectPrimitive.Root;
export const SelectGroup = SelectPrimitive.Group;
export const SelectValue = SelectPrimitive.Value;

export const SelectTrigger = forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    data-pp-select-trigger
    className={cn(
      "flex h-9 w-full items-center justify-between",
      "border border-[var(--pp-border)] bg-transparent px-3",
      "font-[var(--pp-font-family)] text-[var(--pp-font-md)] text-[var(--pp-text)]",
      "placeholder:text-[var(--pp-text-muted)]",
      "transition-[border-color,box-shadow]",
      "hover:border-[var(--pp-text-muted)]",
      "focus:outline-none focus-visible:border-[var(--primary)] focus-visible:ring-1 focus-visible:ring-[var(--primary)]",
      "disabled:cursor-not-allowed disabled:opacity-50",
      "[&>span]:line-clamp-1",
      className,
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <span aria-hidden className="ml-2 text-[var(--pp-text-muted)]">▾</span>
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

export const SelectContent = forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      position={position}
      data-pp-select-content
      className={cn(
        "relative z-50 max-h-96 min-w-[8rem] overflow-hidden",
        position === "popper" &&
          "data-[side=bottom]:translate-y-1 data-[side=top]:-translate-y-1",
        className,
      )}
      {...props}
    >
      <Panel variant="solid">
        <SelectPrimitive.Viewport
          className={cn(
            "p-1 text-[var(--pp-text)]",
            position === "popper" &&
              "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]",
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
      </Panel>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

export const SelectLabel = forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn(
      "px-2 py-1.5 text-[var(--pp-font-sm)] uppercase tracking-[var(--pp-tracking-caps)] text-[var(--pp-text-muted)]",
      className,
    )}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

export const SelectItem = forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex w-full cursor-default select-none items-center",
      "py-1.5 pl-8 pr-2 text-[var(--pp-font-md)] outline-none",
      "focus:bg-[var(--pp-bg)] focus:text-[var(--pp-text)]",
      "data-[state=checked]:text-[var(--accent)]",
      "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className,
    )}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <span aria-hidden>›</span>
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

export const SelectSeparator = forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-[var(--pp-border)]", className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;
